import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';

import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';

import { topBarHeight } from '../../../main-deprecated/theme';
import BackdropCircularProgress from '../../components/BackdropCircularProgress';
import { BLACK, PRIMARY_PURPLE } from '../../theme/colorConstants';
import { PRIMARY_FONT } from '../../theme/fontConstants';
import { searchParamOptions } from '../app/RouteBuilding/routeConstants';

import AdditionalInfoTab from './components/additionalInfo/AdditionalInfoTab';
import ApprovalCard from './components/ApprovalCard';
import LegalInfoTab from './components/legalInfo/LegalInfoTab';
import OverviewTab from './components/overview/OverviewTab';
import PositionSummaryCard from './components/PositionSummaryCard';
import PositionTemplateDetailHeader from './components/PositionTemplateDetailHeader';
import ShiftDetailsTab from './components/shiftDetails/ShiftDetailsTab';
import { TAB_ORDER, TABS } from './constants';
import { getId } from './helpers';
import {
  clearPositionTemplateDetail,
  getPositionTemplateDetail,
} from './positionTemplateDetailReducer';
import {
  selectHasPositionTemplateDetailError,
  selectIsPositionTemplateDetailLoading,
  selectPositionTemplateDetail,
} from './positionTemplateDetailSelectors';
import useMissingPairingLocations from './useMissingPairingLocations';
import usePositionTemplateFieldSave from './usePositionTemplateFieldSave';

const TAB_LABEL_IDS = {
  [TABS.OVERVIEW]: 'pt.detail.tab.overview',
  [TABS.SHIFT_DETAILS]: 'pt.detail.tab.shiftDetails',
  [TABS.ADDITIONAL_INFO]: 'pt.detail.tab.additionalInfo',
  [TABS.LEGAL_INFO]: 'pt.detail.tab.legalInfo',
};

const pageSx = {
  backgroundColor: BLACK[0],
  display: 'flex',
  flexDirection: 'column',
  height: `calc(100vh - ${topBarHeight})`,
  overflowY: 'auto',
  width: '100%',
};

const notFoundSx = { color: BLACK[70], padding: '24px' };

const layoutSx = {
  display: 'grid',
  gap: '24px',
  gridTemplateColumns: { xs: '1fr', md: '280px minmax(0, 1fr)' },
  padding: '20px 24px',
};

const sidebarSx = { display: 'flex', flexDirection: 'column', gap: '16px' };

const mainSx = { ...sidebarSx, minWidth: 0 };

const tabsSx = {
  borderBottom: `1px solid ${BLACK[30]}`,
  minHeight: '40px',
  '& .MuiTabs-indicator': { backgroundColor: PRIMARY_PURPLE[70] },
};

const tabSx = {
  color: BLACK[70],
  fontFamily: PRIMARY_FONT[500],
  fontSize: '14px',
  minHeight: '40px',
  textTransform: 'none',
  '&.Mui-selected': { color: PRIMARY_PURPLE[70] },
};

const PositionTemplateDetailPage = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { id } = useParams();
  const positionTemplate = useSelector(selectPositionTemplateDetail);
  const isLoading = useSelector(selectIsPositionTemplateDetailLoading);
  const hasError = useSelector(selectHasPositionTemplateDetailError);
  const { saveField, getStatus } = usePositionTemplateFieldSave();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get(searchParamOptions.ACTIVE_TAB);
  const activeTab = TAB_ORDER.includes(tabParam) ? tabParam : TABS.OVERVIEW;

  const handleTabChange = (event, tab) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set(searchParamOptions.ACTIVE_TAB, tab);
    setSearchParams(nextParams, { replace: true });
  };

  useEffect(() => {
    dispatch(
      getPositionTemplateDetail({
        filters: [{ field: '_id', operation: 'equalsID', value: id }],
      })
    );
    return () => {
      dispatch(clearPositionTemplateDetail());
    };
  }, [dispatch, id]);

  const isLoaded = getId(positionTemplate) === id;
  useMissingPairingLocations(isLoaded ? positionTemplate : undefined);
  const sectionProps = { positionTemplate, saveField, getStatus };

  return (
    <Box data-testid="position-template-detail-page" sx={pageSx}>
      {isLoading && <BackdropCircularProgress />}
      {hasError && !isLoading && (
        <Typography sx={notFoundSx}>{intl.formatMessage({ id: 'pt.detail.notFound' })}</Typography>
      )}
      {isLoaded && (
        <>
          <PositionTemplateDetailHeader positionTemplate={positionTemplate} />
          <Box sx={layoutSx}>
            <Box sx={sidebarSx}>
              <ApprovalCard {...sectionProps} />
              <PositionSummaryCard positionTemplate={positionTemplate} />
            </Box>
            <Box sx={mainSx}>
              <Tabs value={activeTab} onChange={handleTabChange} sx={tabsSx}>
                {TAB_ORDER.map((tab) => (
                  <Tab
                    key={tab}
                    value={tab}
                    label={intl.formatMessage({ id: TAB_LABEL_IDS[tab] })}
                    sx={tabSx}
                  />
                ))}
              </Tabs>
              {activeTab === TABS.OVERVIEW && <OverviewTab {...sectionProps} />}
              {activeTab === TABS.SHIFT_DETAILS && <ShiftDetailsTab {...sectionProps} />}
              {activeTab === TABS.ADDITIONAL_INFO && <AdditionalInfoTab {...sectionProps} />}
              {activeTab === TABS.LEGAL_INFO && <LegalInfoTab {...sectionProps} />}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default PositionTemplateDetailPage;
